import { useSupplierPagesInfiniteQuery, useSupplierQuery } from '@/api/endpoints';
import { Select } from '@/components/ui';
import { pickerPages, usePickerSearch } from '@/lib/pickerPages';

interface SupplierSelectProps {
  label?: string;
  value: string;
  onChange: (supplierId: string) => void;
  /** What the blank option means here — "Select a supplier", "Not a supplier". */
  placeholder?: string;
  hint?: string;
  required?: boolean;
  /** Show the supplier code beside the name. */
  withCode?: boolean;
}

/**
 * A supplier dropdown that pages and searches on the server, so a business
 * with more suppliers than one page still reaches every one of them.
 */
export function SupplierSelect({
  label = 'Supplier',
  value,
  onChange,
  placeholder = 'Select a supplier',
  hint,
  required,
  withCode,
}: SupplierSelectProps) {
  const [search, setSearch] = usePickerSearch();
  const { items: suppliers, picker } = pickerPages(
    useSupplierPagesInfiniteQuery({ isActive: true, search }),
    setSearch,
  );

  // The chosen supplier may not be on a page loaded yet — an older record
  // being edited, say — and still has to be shown by name.
  const loaded = !value || suppliers.some((supplier) => supplier.id === value);
  const { data: chosen } = useSupplierQuery(value, { skip: loaded });
  const options = chosen && !loaded && !search ? [chosen, ...suppliers] : suppliers;

  return (
    <Select
      label={label}
      required={required}
      hint={hint}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      {...picker}
    >
      <option value="">{placeholder}</option>
      {options.map((supplier) => (
        <option key={supplier.id} value={supplier.id}>
          {withCode ? `${supplier.name} (${supplier.code})` : supplier.name}
        </option>
      ))}
    </Select>
  );
}
